import React, { createContext, useContext, useEffect, useMemo } from "react";
import eventTracker from "@rapid/analytics/EventTracker";
import { AppSource } from "@rapid/analytics/types";

export enum RapidEmbedSource {
    App = "app",
    Universe = "universe",
    Admin = "admin"
}

export interface EmbeddedInfo {
    isEmbedded: boolean;
    source: RapidEmbedSource | null;
    appSource: AppSource;
}

const defaultInfo: EmbeddedInfo = { isEmbedded: false, source: null, appSource: AppSource.RAPID };

const EmbeddedContext = createContext<EmbeddedInfo>(defaultInfo);

export function EmbeddedProvider({
    source,
    children
}: {
    source?: RapidEmbedSource;
    children: React.ReactNode;
}) {
    const info = useMemo<EmbeddedInfo>(() => {
        if (!source) return defaultInfo;
        return { isEmbedded: true, source, appSource: `rapid:embedded:${source}` as AppSource };
    }, [source]);

    useEffect(() => {
        eventTracker.setAppSource(info.appSource);
    }, [info.appSource]);

    return <EmbeddedContext.Provider value={info}>{children}</EmbeddedContext.Provider>;
}

export function useEmbedded(): EmbeddedInfo {
    return useContext(EmbeddedContext);
}

export function useIsEmbedded(): boolean {
    return useContext(EmbeddedContext).isEmbedded;
}
